import { useState, useEffect, useRef } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import type { Field } from "@project/admin/components/resource-table";
import { galleryService } from "@project/services/admin/gallery.service";
import { galleryCategoryService } from "@project/services/admin/gallery-category.service";
import type {
  GalleryItem,
  GalleryPayload,
  GalleryCategory,
  GalleryCategoryPayload,
} from "@project/types/gallery";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@project/components/ui/tabs";
import { ImageIcon, FolderTree, Upload } from "lucide-react";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Switch } from "@project/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { resolveMediaUrl } from "@project/lib/utils";

// ─── Fields for Gallery Photos ───────────────────────────────────────────────
const galleryFields: Field[] = [
  {
    name: "image",
    label: "Image",
    type: "image",
    showInTable: true,
  },
  {
    name: "title",
    label: "Title",
    required: true,
    showInTable: true,
    placeholder: "e.g. Annual Event 2026",
  },
  {
    name: "category",
    label: "Category",
    type: "select",
    required: true,
    showInTable: true,
  },
  {
    name: "order_by",
    label: "Sort Order",
    type: "number",
    showInTable: true,
    placeholder: "1",
  },
  {
    name: "active",
    label: "Active",
    type: "boolean",
    showInTable: true,
  },
];

// ─── Fields for Gallery Categories ───────────────────────────────────────────
const categoryFields: Field[] = [
  {
    name: "category_name",
    label: "Category Name",
    required: true,
    showInTable: true,
    placeholder: "e.g. Events, Campus, Spiritual",
  },
  {
    name: "order_by",
    label: "Sort Order",
    type: "number",
    showInTable: true,
    placeholder: "1",
  },
  {
    name: "active",
    label: "Active",
    type: "boolean",
    showInTable: true,
  },
];

function toCategoryPayload(
  payload: Record<string, unknown>
): GalleryCategoryPayload {
  return {
    category_name: String(payload.category_name ?? "").trim(),
    order_by:
      payload.order_by !== undefined && payload.order_by !== ""
        ? Number(payload.order_by)
        : 1,
    active: payload.active === true || payload.active === "true",
  };
}

function toGalleryPayload(payload: Record<string, unknown>): GalleryPayload {
  return {
    title: String(payload.title ?? "").trim(),
    category: payload.category ? String(payload.category).trim() : "",
    order_by:
      payload.order_by !== undefined && payload.order_by !== ""
        ? Number(payload.order_by)
        : 1,
    active: payload.active === true || payload.active === "true",
    image:
      payload.image instanceof File
        ? payload.image
        : typeof payload.image === "string"
          ? payload.image
          : typeof payload.image_url === "string"
            ? payload.image_url
            : undefined,
  };
}

// ─── Custom Form for Gallery Photo (with file drag/drop preview) ──────────────
function GalleryForm({
  editing,
  setEditing,
  isEditMode,
  categories,
}: {
  editing: Partial<GalleryItem> | null;
  setEditing: React.Dispatch<React.SetStateAction<Partial<GalleryItem> | null>>;
  isEditMode: boolean;
  categories: { value: string; label: string }[];
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(() => {
    const raw = editing?.image_url || editing?.image;
    if (raw) {
      if (typeof raw === "string") {
        return resolveMediaUrl(raw);
      }
      if (raw instanceof File) {
        return URL.createObjectURL(raw);
      }
    }
    return null;
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setEditing(prev => ({ ...(prev || {}), image: file as any }));
      setPreview(URL.createObjectURL(file));
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-foreground">
          Title <span className="text-destructive">*</span>
        </Label>
        <Input
          required
          value={editing?.title ?? ""}
          onChange={e =>
            setEditing(prev => ({ ...(prev || {}), title: e.target.value }))
          }
          placeholder="e.g. Annual Event 2026 / Morning Prayer"
          className="bg-muted/40 rounded-xl text-xs"
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-foreground">
          Category <span className="text-destructive">*</span>
        </Label>
        <Select
          value={editing?.category ? String(editing.category) : ""}
          onValueChange={val =>
            setEditing(prev => ({ ...(prev || {}), category: val }))
          }
        >
          <SelectTrigger className="bg-muted/40 rounded-xl text-xs">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map(c => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-foreground">
          Photo Image {!isEditMode && <span className="text-destructive">*</span>}
        </Label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
        <div
          onClick={() => fileInputRef.current?.click()}
          className="group relative flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border bg-muted/20 p-4 transition-all hover:border-primary/60 hover:bg-muted/40"
        >
          {preview ? (
            <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-border">
              <img
                src={preview}
                alt="Preview"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                <span className="flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1 text-xs font-bold text-white backdrop-blur-sm">
                  <Upload className="h-3.5 w-3.5" /> Change Photo
                </span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-4 text-center">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary">
                <Upload className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-bold text-foreground">
                  Click to upload an image
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  PNG, JPG, WEBP up to 10MB
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 items-center gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-foreground">
            Sort Order
          </Label>
          <Input
            type="number"
            min="0"
            value={editing?.order_by ?? 1}
            onChange={e =>
              setEditing(prev => ({
                ...(prev || {}),
                order_by: parseInt(e.target.value, 10) || 1,
              }))
            }
            className="bg-muted/40 rounded-xl text-xs"
          />
        </div>

        <div className="space-y-1.5 flex flex-col justify-end">
          <Label className="text-xs font-semibold text-foreground mb-1">
            Active Status
          </Label>
          <div className="flex items-center gap-2.5 h-10">
            <Switch
              checked={editing?.active !== false}
              onCheckedChange={checked =>
                setEditing(prev => ({ ...(prev || {}), active: checked }))
              }
            />
            <span className="text-xs font-medium text-foreground">
              {editing?.active !== false ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab Configuration ────────────────────────────────────────────────────────
type TabId = "gallery" | "categories";

const tabs: { id: TabId; label: string; icon: typeof ImageIcon }[] = [
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "categories", label: "Categories", icon: FolderTree },
];

export default function GalleryAdminPage() {
  const [activeTab, setActiveTab] = useState<TabId>("gallery");
  const [categoriesList, setCategoriesList] = useState<
    { value: string; label: string }[]
  >([]);

  const loadCategories = async () => {
    try {
      const res = await galleryCategoryService.listPage({
        page: 1,
        page_size: 200,
      });
      const options = (res.data || []).map(c => ({
        value: String(c.id),
        label: c.category_name,
      }));
      setCategoriesList(options);
      return options;
    } catch (err) {
      console.error("Failed to load categories for select", err);
      return [];
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  return (
    <AdminShell title="Gallery" subtitle="Photo gallery management">
      <Tabs
        value={activeTab}
        onValueChange={val => setActiveTab(val as TabId)}
        variant="line"
        className="space-y-4"
      >
        <div className="-mx-1 overflow-x-auto px-1 pb-1">
          <TabsList>
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <TabsTrigger key={tab.id} value={tab.id} className="shrink-0">
                  <Icon className="h-4 w-4 shrink-0" />
                  {tab.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {/* Tab 1: Gallery Photos */}
        {activeTab === "gallery" && (
          <ApiResourceTable<GalleryItem>
            title="Photo"
            labelField="title"
            fields={galleryFields}
            searchFields={["title", "category"]}
            defaultValues={{ active: true, order_by: 1 }}
            enableCsvExport
            csvFileName="gallery-photos"
            lookupOptions={{ category: categoriesList }}
            lookupLoaders={{ category: loadCategories }}
            list={query => galleryService.listPage(query)}
            create={payload => galleryService.create(toGalleryPayload(payload))}
            update={(id, payload) =>
              galleryService.update(id, toGalleryPayload(payload))
            }
            remove={id => galleryService.delete(id)}
            buildPayload={editing => {
              if (!editing) return null;
              return {
                title: editing.title,
                category: editing.category,
                order_by: editing.order_by ?? 1,
                active: editing.active ?? true,
                image: (editing as any).image,
              };
            }}
            renderForm={({ editing, setEditing, isEditMode }) => (
              <GalleryForm
                editing={editing}
                setEditing={setEditing}
                isEditMode={isEditMode}
                categories={categoriesList}
              />
            )}
          />
        )}

        {/* Tab 2: Categories */}
        {activeTab === "categories" && (
          <ApiResourceTable<GalleryCategory>
            title="Category"
            labelField="category_name"
            fields={categoryFields}
            searchFields={["category_name"]}
            defaultValues={{ active: true, order_by: 1 }}
            enableCsvExport
            csvFileName="gallery-categories"
            list={query => galleryCategoryService.listPage(query)}
            create={payload =>
              galleryCategoryService.create(toCategoryPayload(payload))
            }
            update={(id, payload) =>
              galleryCategoryService.update(id, toCategoryPayload(payload))
            }
            remove={id => galleryCategoryService.delete(id)}
          />
        )}
      </Tabs>
    </AdminShell>
  );
}
